import type { ReactNode } from 'react';
import { sx } from '../../ui/designStyles';

/**
 * The pieces the V9 Performance tabs share (Survey ready, Admissions, Credentials), in the
 * design's own styles (ConsoleV9 isSurveyTab / isAdmitTab / isCredTab).
 */

/** The scrolling page of a V9 tab. */
export const V9_PANE = 'flex:1 1 auto;min-height:0;overflow-y:auto;display:flex;flex-direction:column;gap:16px;padding:12px 2px 24px;min-width:0;';
/** A section under a rule. */
export const V9_SECTION = 'border-top:1px solid rgba(34,39,46,.14);padding-top:10px;display:flex;flex-direction:column;min-width:0;';
/** A section's small capitals heading. */
export const V9_HEAD = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:700;';
/** An outlined blue button (design: 36px, 16px). */
export const V9_BTN =
  'display:inline-flex;align-items:center;justify-content:center;min-height:36px;padding:6px 12px;box-sizing:border-box;border-radius:8px;border:1px solid rgba(47,92,151,.4);color:#2F5C97;font-size:16px;font-weight:700;text-decoration:none;line-height:1.25;white-space:nowrap;';
/** A filled blue button (design: 40px, 16px). */
export const V9_PRI = 'display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:8px;background:#2F5C97;color:#fff;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;';
/** A plain outlined button (design: 40px, 16px, grey rule). */
export const V9_GHOST = 'display:inline-flex;align-items:center;min-height:40px;padding:0 12px;border-radius:8px;border:1px solid rgba(34,39,46,.18);color:#22272E;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;';
/** A list dot (design dot()). */
export const v9Dot = (c: string, top = true) => 'width:9px;height:9px;border-radius:50%;flex:0 0 9px;' + (top ? 'margin-top:6px;' : '') + 'background:' + c + ';';

export const NOT_CONNECTED = 'Not connected yet';
/** The words every empty section shows, the same as the phone's (es-phone has the Spanish). */
export const NOT_CONNECTED_LINE = 'Not connected yet. This fills in once the building’s records are connected to Aithora.';

/** A section heading with an optional note beside it. */
export function SectionHead({ title, note, children }: { title: string; note?: string | null; children?: ReactNode }) {
  return (
    <div style={sx('display:flex;align-items:baseline;gap:8px 12px;flex-wrap:wrap;margin-bottom:4px;min-width:0;')}>
      <span style={sx(V9_HEAD)}>{title}</span>
      {note ? <span style={sx('font-size:16px;color:#54606C;')}>{note}</span> : null}
      {children}
    </div>
  );
}

/** The honest empty line: no number, no sample, just what is missing. */
export function NotConnected({ text = NOT_CONNECTED_LINE, testId }: { text?: string; testId?: string }) {
  return (
    <div data-testid={testId ?? 'not-connected'} style={sx('font-size:16px;color:#54606C;line-height:1.45;padding:6px 0;max-width:96ch;text-wrap:pretty;')}>
      {text}
    </div>
  );
}

/** "Read-only as Super Admin" where a read-only seat would have had buttons. */
export function ReadOnlyLine({ text, testId = 'perf-readonly' }: { text: string; testId?: string }) {
  return (
    <span data-testid={testId} style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>
      {text}
    </span>
  );
}
